/**
 * Edit-mode session state for a dashboard (Beta-1 plan P3-16) — a pure reducer so Cancel,
 * Done and undo can be unit-tested without React. `useDashboardEditor` wraps it.
 *
 *   state = {
 *     layout      the layout being shown (edited copy while editing)
 *     editing     Customize pressed, Done/Cancel not yet
 *     snapshot    the layout as it was when editing began (Cancel restores it) or null
 *     removed     last removed item + its title, offered for undo (P3-16 snackbar) or null
 *     lastAdded   id of the item the gallery just placed (grid scrolls to + highlights it, P3-17)
 *   }
 *
 * Nothing here persists anything: Done hands the final layout to the caller (P3-19 saves it).
 */
import { addItem, layoutsEqual, removeItem, restoreItem } from './layoutModel.js';

export const EDITOR_ACTIONS = Object.freeze({
  BEGIN: 'begin',
  DONE: 'done',
  CANCEL: 'cancel',
  SET_LAYOUT: 'set-layout',
  REPLACE: 'replace', // external load (Firestore, reset to defaults) — also clears any edit session
  REMOVE: 'remove',
  UNDO_REMOVE: 'undo-remove',
  DISMISS_UNDO: 'dismiss-undo',
  ADD: 'add', // { widget, size?, props? } from the gallery (P3-17)
  CLEAR_ADDED: 'clear-added',
});

export function createEditorState(layout) {
  return { layout, editing: false, snapshot: null, removed: null, lastAdded: null };
}

/** True while editing and the layout differs from the snapshot. */
export function isEditorDirty(state) {
  return Boolean(state.editing && state.snapshot && !layoutsEqual(state.layout, state.snapshot));
}

export function editorReducer(state, action) {
  switch (action.type) {
    case EDITOR_ACTIONS.BEGIN:
      if (state.editing) return state;
      return { ...state, editing: true, snapshot: state.layout, removed: null, lastAdded: null };

    case EDITOR_ACTIONS.DONE:
      if (!state.editing) return state;
      return { ...state, editing: false, snapshot: null, removed: null, lastAdded: null };

    case EDITOR_ACTIONS.CANCEL:
      if (!state.editing) return state;
      return { layout: state.snapshot || state.layout, editing: false, snapshot: null, removed: null, lastAdded: null };

    case EDITOR_ACTIONS.SET_LAYOUT: {
      const next = typeof action.layout === 'function' ? action.layout(state.layout) : action.layout;
      if (!next || next === state.layout) return state;
      return { ...state, layout: next };
    }

    case EDITOR_ACTIONS.REPLACE:
      return createEditorState(action.layout);

    case EDITOR_ACTIONS.REMOVE: {
      const item = state.layout.items.find((it) => it.i === action.i);
      if (!item) return state;
      return {
        ...state,
        layout: removeItem(state.layout, action.i),
        removed: { item, title: action.title || item.widgetId },
      };
    }

    case EDITOR_ACTIONS.UNDO_REMOVE:
      if (!state.removed) return state;
      return { ...state, layout: restoreItem(state.layout, state.removed.item), removed: null };

    case EDITOR_ACTIONS.DISMISS_UNDO:
      return state.removed ? { ...state, removed: null } : state;

    case EDITOR_ACTIONS.ADD: {
      if (!action.widget) return state;
      const { layout, item } = addItem(state.layout, action.widget, { size: action.size, props: action.props });
      return { ...state, layout, lastAdded: item.i };
    }

    case EDITOR_ACTIONS.CLEAR_ADDED:
      return state.lastAdded ? { ...state, lastAdded: null } : state;

    default:
      return state;
  }
}
